/**
 * Dispatch Groups settings — wraps the generic CrudPage to layer an
 * "Assign Assets" bulk action on top of the base CRUD surface. The action
 * opens a multi-select dialog listing every active Atlas asset; checking
 * an asset maps it to the chosen group, and unchecking removes the link.
 * Since `Asset.dispatchGroup` is a single ref, reassigning from a different
 * group happens automatically — no separate unlink step.
 *
 * The action is gated client-side to a single selected group (it doesn't
 * make sense to assign assets to N groups at once, since each asset can
 * only belong to one). Multi-select still works for Delete Selected and
 * any other future bulk actions.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Truck, GripVertical, LayoutList, LayoutGrid, Loader2 } from 'lucide-react';
import {
  draggable, dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { CrudPage, type CrudConfig } from '@/components/shared/crud-page';
import { cn } from '@/lib/utils';
import { CRUD_CONFIGS } from './configs';

interface AssetRow {
  id: string;
  fleetNumber: string | null;
  registrationNumber: string | null;
  make: string | null;
  model: string | null;
  dispatchGroup: { id: string; name: string } | null;
  showInDispatch?: boolean;
}

interface DispatchGroupRow {
  id: string;
  name: string;
}

/** Subscribe to Tailwind's `lg` breakpoint (≥ 1024px). Used to gate the
 *  drag-mode toggle: below this width, the dialog forces checkbox mode
 *  because the default pragmatic-drag-and-drop adapter is mouse/keyboard
 *  only and the 3-column DnD UI won't fit a phone anyway. */
function useIsLgViewport(): boolean {
  const [isLg, setIsLg] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = () => setIsLg(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isLg;
}

/** Buckets an asset into one of three drag-and-drop columns based on its
 *  state at load time and whether the user has it currently picked.
 *
 *  - `in-group`     — picked: will end up in this group after Confirm.
 *  - `other-group`  — currently assigned to a different group AND not picked:
 *                     untouched by Confirm; stays in its existing group.
 *  - `unassigned`   — has no group OR was originally in this group but was
 *                     removed: ends up with no dispatch group after Confirm.
 */
type Column = 'unassigned' | 'other-group' | 'in-group';
function columnFor(a: AssetRow, picked: Set<string>, groupId: string): Column {
  if (picked.has(a.id)) return 'in-group';
  if (a.dispatchGroup && a.dispatchGroup.id !== groupId) return 'other-group';
  return 'unassigned';
}

/** True when the user has marked this asset for assignment AND it wasn't
 *  already a member of the target group at load time. These are the rows
 *  the Confirm will actually CHANGE — used for both the highlight in the
 *  UI and the scope of the "Also enable Display in Scheduler" toggle on
 *  the server. Pre-existing members that stay picked are NOT new. */
function isNewlyAddedTo(a: AssetRow, picked: Set<string>, groupId: string): boolean {
  return picked.has(a.id) && a.dispatchGroup?.id !== groupId;
}

/** Where a card belongs when NOT picked. Used to constrain drops out of
 *  the in-group column so a card always lands on its "home" column —
 *  faithfully reflecting the server outcome:
 *    • asset was unassigned or in THIS group → home = Unassigned
 *      (removing from picked unassigns it server-side)
 *    • asset was in another group           → home = Other Groups
 *      (removing from picked is a no-op; it stays in its original group)
 *  This is what stops the surprising "drag from Other Groups → In Group
 *  → Unassigned" path that previously snapped the card back to Other
 *  Groups anyway. Now the Unassigned column refuses that drop outright. */
function originalColumnFor(a: AssetRow, groupId: string): Column {
  if (!a.dispatchGroup || a.dispatchGroup.id === groupId) return 'unassigned';
  return 'other-group';
}

/** Draggable asset chip used in DnD mode. Both `column` (where the card
 *  is right now) and `originalColumn` (the card's "home" if removed from
 *  picked) are baked into the drag payload so drop targets can constrain
 *  which cards they accept — see DropColumn.accepts. */
function DraggableAssetCard({
  asset, column, originalColumn, otherGroupName, isNew,
}: {
  asset: AssetRow;
  column: Column;
  originalColumn: Column;
  otherGroupName: string | null;
  /** Highlight rows the Confirm will actually change. See isNewlyAddedTo. */
  isNew: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return draggable({
      element: el,
      getInitialData: () => ({ assetId: asset.id, column, originalColumn }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    });
  }, [asset.id, column, originalColumn]);
  return (
    <div
      ref={ref}
      className={cn(
        'group flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm shadow-sm',
        'cursor-grab active:cursor-grabbing select-none',
        'hover:border-primary/50 hover:shadow',
        isNew ? 'bg-emerald-50 border-emerald-300' : 'bg-card',
        dragging && 'opacity-40',
      )}
    >
      <GripVertical className={cn('h-3.5 w-3.5 shrink-0', isNew ? 'text-emerald-700' : 'text-muted-foreground')} />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {asset.fleetNumber || '—'}
          {asset.registrationNumber && (
            <span className="text-muted-foreground font-normal ml-1.5">{asset.registrationNumber}</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {[asset.make, asset.model].filter(Boolean).join(' ') || '—'}
          {column === 'other-group' && otherGroupName && (
            <span className="text-amber-700"> · {otherGroupName}</span>
          )}
          {isNew && (
            <span className="text-emerald-700 font-medium"> · new</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Centered loading state used inside the dialog body while the asset
 *  list query is in flight. Lives outside the columns/list so both
 *  checkbox and drag modes render the same skeleton-free spinner. */
function LoadingPanel({ label = 'Loading assets…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>{label}</span>
    </div>
  );
}

/** A drop target column. `onDropAsset` receives the asset id and the
 *  column the card came from, so the handler can decide whether to apply
 *  any state change. */
function DropColumn({
  title, count, accent, children, onDropAsset, accepts,
}: {
  title: string;
  count: number;
  accent: string;
  children: React.ReactNode;
  onDropAsset: (assetId: string) => void;
  /** Receives both the column the card came from AND the card's home
   *  column (where it lives when not picked). Lets the column reject
   *  drops that would create a visually-correct-but-server-misleading
   *  state — e.g. Unassigned rejects cards whose origin is another
   *  group, since dropping there would leave them in that other group
   *  on the server but show them in Unassigned in the UI. */
  accepts: (fromColumn: Column, originalColumn: Column) => boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [over, setOver] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return dropTargetForElements({
      element: el,
      // Pragmatic Drag-and-Drop's callback args carry a generic `data`
      // record of unknown shape — TS7031 because nothing in our usage
      // narrows it. Annotating the destructured param as `any` matches
      // the package's own examples and keeps the body honest about
      // pulling fields by name out of a loose payload.
      canDrop: ({ source }: any) => {
        const c = source.data.column;
        const oc = source.data.originalColumn;
        return typeof c === 'string' && typeof oc === 'string'
          && accepts(c as Column, oc as Column);
      },
      onDragEnter: () => setOver(true),
      onDragLeave: () => setOver(false),
      onDrop: ({ source }: any) => {
        setOver(false);
        const id = source.data.assetId;
        if (typeof id === 'string') onDropAsset(id);
      },
    });
  }, [accepts, onDropAsset]);
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col rounded-md border overflow-hidden transition-colors',
        over ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'bg-muted/20',
      )}
    >
      <div className={cn('px-3 py-2 border-b text-sm font-medium flex items-center justify-between', accent)}>
        <span>{title}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">
        {children}
      </div>
    </div>
  );
}

function AssignAssetsDialog({
  group, onClose,
}: {
  group: DispatchGroupRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = React.useState('');
  const [unassignedOnly, setUnassignedOnly] = React.useState(false);
  const [setShowInDispatch, setSetShowInDispatch] = React.useState(false);
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  // Desktop = Tailwind's `lg` breakpoint (≥ 1024px). The DnD adapter is
  // mouse/keyboard only and the 3-column layout won't fit a phone, so we
  // force checkbox mode below this threshold even if the user previously
  // picked drag on a wider screen and then resized.
  const isDesktop = useIsLgViewport();
  // The user's chosen mode (only meaningful on desktop). Default `drag` so
  // the more visual layout greets desktop users on first open.
  const [userMode, setUserMode] = React.useState<'checkbox' | 'drag'>('drag');
  const mode: 'checkbox' | 'drag' = isDesktop ? userMode : 'checkbox';
  // Tracks whether we've seeded `picked` from the fetched member list.
  // Without this, every refetch (e.g. after window focus) would clobber
  // in-progress user edits.
  const seededRef = React.useRef(false);

  // Load every active Atlas asset. The list is bounded (a few hundred)
  // so loading the full set client-side avoids a paged picker UX. Any
  // search / "unassigned only" filtering is done locally below.
  const { data: assets = [], isLoading } = useQuery<AssetRow[]>({
    queryKey: ['dispatch-group-assign-assets', group.id],
    queryFn: async () => {
      const r = await api<AssetRow[]>('/fleet/assets?limit=2000&sort=fleetNumber&order=asc');
      return r.data ?? [];
    },
  });

  // Seed `picked` from the assets that are currently members of this group.
  // Done in an effect so the user's subsequent toggles aren't overwritten
  // by re-renders triggered by the query refetching.
  React.useEffect(() => {
    if (seededRef.current) return;
    if (!assets.length) return;
    const initial = new Set<string>();
    for (const a of assets) {
      if (a.dispatchGroup?.id === group.id) initial.add(a.id);
    }
    setPicked(initial);
    seededRef.current = true;
  }, [assets, group.id]);

  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (unassignedOnly && a.dispatchGroup) return false;
      if (!needle) return true;
      const hay = [
        a.fleetNumber, a.registrationNumber, a.make, a.model,
        a.dispatchGroup?.name,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [assets, search, unassignedOnly]);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAllVisible(check: boolean) {
    setPicked((prev) => {
      const next = new Set(prev);
      for (const a of filtered) {
        if (check) next.add(a.id); else next.delete(a.id);
      }
      return next;
    });
  }

  const sync = useMutation({
    mutationFn: async () => {
      const r = await api<{ assigned: number; unassigned: number }>(
        `/master-data/dispatch-groups/${group.id}/sync-members`,
        {
          method: 'POST',
          body: JSON.stringify({
            assetIds: [...picked],
            setShowInDispatch,
          }),
        },
      );
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data!;
    },
    onSuccess: (data) => {
      const parts: string[] = [];
      if (data.assigned) parts.push(`${data.assigned} assigned`);
      if (data.unassigned) parts.push(`${data.unassigned} unassigned`);
      toast(
        `Updated ${group.name}`,
        parts.length ? parts.join(', ') : 'No changes',
        'success',
      );
      qc.invalidateQueries({ queryKey: ['/master-data/dispatch-groups'] });
      qc.invalidateQueries({ queryKey: ['/fleet/assets'] });
      // Also bust the dialog's own cache. React Query's invalidation is
      // prefix-matched, so dropping the `group.id` segment invalidates
      // every cached dispatch-group dialog query — important because the
      // operator may immediately open another group's dialog whose
      // membership numbers also shifted (e.g. an asset reassigned out of
      // it). Without this, reopening the same dialog would re-seed
      // `picked` from the stale snapshot it held before Confirm and the
      // changes would appear lost until a page refresh.
      qc.invalidateQueries({ queryKey: ['dispatch-group-assign-assets'] });
      onClose();
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // For the "select all visible" checkbox in the header — indeterminate
  // when only some visible rows are picked, full when all are.
  const visibleIds = filtered.map((a) => a.id);
  const visiblePicked = visibleIds.filter((id) => picked.has(id)).length;
  const allVisibleChecked = visibleIds.length > 0 && visiblePicked === visibleIds.length;
  const someVisibleChecked = visiblePicked > 0 && visiblePicked < visibleIds.length;

  // Three-column buckets for drag mode. Built off `filtered` so the search
  // input still filters every column. The "unassigned only" toggle is hidden
  // in drag mode (columns already split by assignment state) so we read it
  // only in checkbox mode.
  const buckets = React.useMemo(() => {
    const unassigned: AssetRow[] = [];
    const otherGroup: AssetRow[] = [];
    const inGroup: AssetRow[] = [];
    for (const a of filtered) {
      const col = columnFor(a, picked, group.id);
      if (col === 'in-group') inGroup.push(a);
      else if (col === 'other-group') otherGroup.push(a);
      else unassigned.push(a);
    }
    return { unassigned, otherGroup, inGroup };
  }, [filtered, picked, group.id]);

  function addToGroup(assetId: string) {
    // From any column → ensure picked. Reassignment from another group
    // happens server-side because `dispatchGroup` is a single ref.
    setPicked((prev) => {
      if (prev.has(assetId)) return prev;
      const next = new Set(prev);
      next.add(assetId);
      return next;
    });
  }
  function removeFromGroup(assetId: string) {
    // The "return to home" handler — shared by Unassigned and Other Groups
    // drop targets. Each column's `accepts` predicate filters down to
    // cards whose home matches that column, so the visual landing matches
    // the server outcome.
    setPicked((prev) => {
      if (!prev.has(assetId)) return prev;
      const next = new Set(prev);
      next.delete(assetId);
      return next;
    });
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className={cn(
          'flex flex-col gap-3 lg:gap-4 p-4 sm:p-6',
          // Drag mode needs more horizontal room for the three columns;
          // checkbox mode keeps the narrower modal that fits content tightly.
          mode === 'drag' ? 'max-w-5xl' : 'max-w-2xl',
        )}
      >
        <DialogHeader>
          {/* pr-8 reserves space for the absolute-positioned X close button so
              a long group name doesn't slide underneath it on narrow screens. */}
          <DialogTitle className="pr-8 break-words">Assign Assets to {group.name}</DialogTitle>
        </DialogHeader>

        {/* Toolbar: search + filters + mode toggle. Stacks vertically on
            phones so nothing gets clipped, side-by-side on tablets+. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search fleet #, rego, make, model…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {/* "Unassigned only" is meaningless in drag mode — the columns
              already separate assets by assignment state — so hide it
              there to remove a redundant control. */}
          {mode === 'checkbox' && (
            <label className="flex items-center gap-2 text-sm shrink-0">
              <Switch checked={unassignedOnly} onCheckedChange={setUnassignedOnly} />
              Unassigned only
            </label>
          )}
          {/* Mode toggle — desktop-only because the default DnD adapter
              doesn't ship touch support, and the 3-column layout is
              unusable on a phone regardless. Below `lg` the dialog forces
              checkbox mode regardless of `userMode`, so this control stays
              hidden. */}
          <div className="hidden lg:inline-flex shrink-0 rounded-md border p-0.5 bg-background">
            <button
              type="button"
              onClick={() => setUserMode('drag')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                mode === 'drag' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent',
              )}
              aria-pressed={mode === 'drag'}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Drag
            </button>
            <button
              type="button"
              onClick={() => setUserMode('checkbox')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                mode === 'checkbox' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent',
              )}
              aria-pressed={mode === 'checkbox'}
            >
              <LayoutList className="h-3.5 w-3.5" /> Checkbox
            </button>
          </div>
        </div>

        {/* Status line — the "uncheck to remove" hint hides on phones to free
            vertical space; the same intent is communicated by the pre-checked
            rows themselves. Hidden entirely in drag mode where the column
            structure makes the hint redundant. */}
        {mode === 'checkbox' && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            <div>
              {isLoading
                ? 'Loading assets…'
                : `${filtered.length} asset${filtered.length === 1 ? '' : 's'} shown · ${picked.size} selected`}
            </div>
            <div className="hidden sm:block">
              Current members of this group are pre-checked. Uncheck to remove.
            </div>
          </div>
        )}

        {mode === 'checkbox' ? (
          /* Asset list — div-based rows so columns flow naturally instead of
             forcing a fixed table width that overflows narrow viewports. The
             "Current Group" column is only rendered on md+; on phones the
             other-group label tucks under fleet/rego on a second line. */
          <div className="border rounded-md overflow-y-auto flex-1 min-h-[200px] max-h-[55vh] lg:max-h-[420px]">
            <div className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm border-b flex items-center gap-3 px-3 py-2 text-xs font-medium">
              <Checkbox
                aria-label="Select all visible"
                checked={allVisibleChecked ? true : someVisibleChecked ? 'indeterminate' : false}
                onCheckedChange={(v) => toggleAllVisible(!!v)}
                disabled={isLoading}
              />
              <span className="flex-1">Asset</span>
              <span className="hidden md:block w-40 shrink-0">Current Group</span>
            </div>

            {isLoading && <LoadingPanel />}
            {!isLoading && filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-muted-foreground">No assets match.</div>
            )}

            <div className="divide-y">
              {filtered.map((a) => {
                const checked = picked.has(a.id);
                const inOtherGroup = a.dispatchGroup && a.dispatchGroup.id !== group.id;
                const inThisGroup = a.dispatchGroup?.id === group.id;
                const isNew = isNewlyAddedTo(a, picked, group.id);
                return (
                  <div
                    key={a.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 cursor-pointer',
                      // Tinted background + left border accent so the rows
                      // the Confirm will actually CHANGE stand out from
                      // pre-existing members that are merely staying picked.
                      isNew
                        ? 'bg-emerald-50 hover:bg-emerald-100 border-l-2 border-l-emerald-400'
                        : 'hover:bg-accent/40',
                    )}
                    onClick={() => toggle(a.id)}
                  >
                    <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggle(a.id)}
                        aria-label={`Select ${a.fleetNumber ?? a.id}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {a.fleetNumber || '—'}
                        {a.registrationNumber && (
                          <span className="text-muted-foreground font-normal ml-2">{a.registrationNumber}</span>
                        )}
                        {isNew && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 uppercase tracking-wide">
                            New
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[a.make, a.model].filter(Boolean).join(' ') || '—'}
                        {/* On phones the desktop "Current Group" column is
                            hidden, so surface the other-group warning inline
                            to keep the reassignment intent obvious. */}
                        {inOtherGroup && (
                          <span className="md:hidden text-amber-700"> · in {a.dispatchGroup!.name}</span>
                        )}
                      </div>
                    </div>
                    <div className="hidden md:block w-40 shrink-0 text-xs truncate">
                      {inThisGroup
                        ? <span>In this group</span>
                        : inOtherGroup
                          ? <span className="text-amber-700">{a.dispatchGroup!.name}</span>
                          : <span className="text-muted-foreground">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Drag-and-drop mode — three columns reflect the future state of
             the asset's `dispatchGroup` after Confirm. Each column only
             accepts the cards whose "home" matches it, so a card always
             lands where it'll actually end up server-side (e.g. a card
             originally in another group can only fall back to Other
             Groups, never to Unassigned). */
          <div className="flex flex-col gap-2 flex-1 min-h-0">
            {/* The instructional helper only makes sense once the columns
                have content; during the initial query it's noise next to
                the spinner. */}
            {!isLoading && (
              <div className="text-xs text-muted-foreground">
                Drag cards into <b>In this group</b> to assign them. To revert, drag a card back to the column it came from — assets originally in another group go back to <b>Other Groups</b>, others go to <b>Unassigned</b>.
              </div>
            )}
            {isLoading ? (
              /* Centered spinner standing in for the columns while the
                 assets list query is in flight — avoids three side-by-side
                 "Empty" panels that would otherwise misleadingly suggest
                 there's nothing to assign. */
              <div className="flex-1 min-h-[320px] border rounded-md flex items-center justify-center bg-muted/10">
                <LoadingPanel />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 flex-1 min-h-[320px] max-h-[60vh]">
                <DropColumn
                  title="Unassigned"
                  count={buckets.unassigned.length}
                  accent="bg-muted/40"
                  /* Accepts only cards whose home is Unassigned — i.e. assets
                     originally with no group OR originally in this group.
                     Rejects cards originally in another group so the visual
                     can't lie about the server outcome. */
                  accepts={(from, origin) => from === 'in-group' && origin === 'unassigned'}
                  onDropAsset={removeFromGroup}
                >
                  {buckets.unassigned.map((a) => (
                    <DraggableAssetCard
                      key={a.id}
                      asset={a}
                      column="unassigned"
                      originalColumn={originalColumnFor(a, group.id)}
                      otherGroupName={null}
                      isNew={false}
                    />
                  ))}
                  {buckets.unassigned.length === 0 && (
                    <div className="text-xs text-muted-foreground italic px-1 py-2">Empty</div>
                  )}
                </DropColumn>

                <DropColumn
                  title="Other Groups"
                  count={buckets.otherGroup.length}
                  accent="bg-amber-50 text-amber-900"
                  /* Accepts ONLY cards whose home is Other Groups — i.e. an
                     "undo claim" path for assets originally in another group.
                     Dropping returns the card to its origin, which is a no-op
                     on save (server only touches assets in `picked` or those
                     currently in THIS group). */
                  accepts={(from, origin) => from === 'in-group' && origin === 'other-group'}
                  onDropAsset={removeFromGroup}
                >
                  {buckets.otherGroup.map((a) => (
                    <DraggableAssetCard
                      key={a.id}
                      asset={a}
                      column="other-group"
                      originalColumn={originalColumnFor(a, group.id)}
                      otherGroupName={a.dispatchGroup?.name ?? null}
                      isNew={false}
                    />
                  ))}
                  {buckets.otherGroup.length === 0 && (
                    <div className="text-xs text-muted-foreground italic px-1 py-2">Empty</div>
                  )}
                </DropColumn>

                <DropColumn
                  title={`In ${group.name}`}
                  count={buckets.inGroup.length}
                  accent="bg-primary/10 text-primary"
                  accepts={(from) => from !== 'in-group'}
                  onDropAsset={addToGroup}
                >
                  {buckets.inGroup.map((a) => (
                    <DraggableAssetCard
                      key={a.id}
                      asset={a}
                      column="in-group"
                      originalColumn={originalColumnFor(a, group.id)}
                      otherGroupName={null}
                      isNew={isNewlyAddedTo(a, picked, group.id)}
                    />
                  ))}
                  {buckets.inGroup.length === 0 && (
                    <div className="text-xs text-muted-foreground italic px-1 py-2">
                      Drop assets here to add them to this group.
                    </div>
                  )}
                </DropColumn>
              </div>
            )}
          </div>
        )}

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={setShowInDispatch}
            onCheckedChange={(v) => setSetShowInDispatch(!!v)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Also enable Display in Scheduler</span>
            <span className="block text-xs text-muted-foreground">
              Applies only to newly assigned assets (highlighted). Existing members of this group are left as-is.
            </span>
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Cancel</Button>
          <Button
            onClick={() => sync.mutate()}
            disabled={sync.isPending || isLoading}
            className="w-full sm:w-auto"
          >
            {sync.isPending ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DispatchGroupsPage() {
  const [assignDialog, setAssignDialog] = React.useState<DispatchGroupRow | null>(null);
  const baseConfig = CRUD_CONFIGS['dispatch-groups'];

  // We need the picked group's name for the dialog title. The bulk action
  // only carries ids, so resolve via the in-memory list the report already
  // fetched (same query key the CrudPage uses).
  const qc = useQueryClient();
  function resolveGroup(id: string): DispatchGroupRow {
    // Walk the React Query cache for any list query keyed on this endpoint
    // and find the row by id. CrudPage stores its list as
    // `{ items: [...], meta }` so we look there first; the fallbacks
    // (`data: [...]`, plain array) are defensive for other call sites
    // that might key the same endpoint differently. The id is still the
    // route param either way — a name fallback only affects the dialog
    // title, not the server payload.
    const entries = qc.getQueriesData<any>({ queryKey: [baseConfig.endpoint] });
    for (const [, data] of entries) {
      const rows: any[] = Array.isArray(data)
        ? data
        : data?.items ?? data?.data ?? [];
      const hit = rows.find?.((r: any) => r?.id === id);
      if (hit) return { id: hit.id, name: hit.name ?? '(unnamed)' };
    }
    return { id, name: '(group)' };
  }

  const config: CrudConfig = {
    ...baseConfig,
    bulkActions: [
      ...(baseConfig.bulkActions ?? []),
      {
        label: 'Assign Assets…',
        actionKey: 'dispatch-group.sync-members',
        onClick: (ids) => {
          if (ids.length !== 1) {
            toast(
              'Select exactly one dispatch group',
              'Assigning assets only makes sense for a single group at a time — each asset can belong to one group.',
              'error',
            );
            return;
          }
          setAssignDialog(resolveGroup(ids[0]));
        },
      },
    ],
    rowActions: [
      ...(baseConfig.rowActions ?? []),
      {
        label: 'Assign Assets',
        actionKey: 'dispatch-group.sync-members',
        inline: true,
        icon: <Truck className="h-4 w-4" />,
        onClick: (row) => setAssignDialog({ id: row.id, name: row.name ?? '(unnamed)' }),
      },
    ],
  };

  return (
    <>
      <CrudPage config={config} />
      {assignDialog && (
        <AssignAssetsDialog
          group={assignDialog}
          onClose={() => setAssignDialog(null)}
        />
      )}
    </>
  );
}
